import { atom, useAtomValue } from 'jotai';

import { useDeepMemo } from '@crossnokaye/ux-common';

import { DisplayRef, Namespace, Ref, Schedule, Sequence } from '@repo/sdk/models/components';

import { readingsFamily } from '@/hooks/query-hooks/streams/gaia-api/use-readings-stream';
import { getLatestPrimitiveValue, LatestValue, LatestValueKind } from '@/types/reading-types';

export interface EnhancedReading {
  displayUnits?: string;
  key: string;
  label?: string;
  rawValue?: LatestValue;
  ref: Ref;
  units?: string;
  value?: string | number | boolean | Schedule | Sequence;
  valueKind?: LatestValueKind;
}

/**
 * Hook to get reading/setting values by reactively reading from Jotai atom families.
 * This bypasses the query cache layer and reads directly from the atoms updated by WebSocket handlers.
 *
 * @param readingRefs - Array of display refs to get values for
 * @returns Array of enhanced readings with current values from Jotai atoms
 */
export const useGetReadingsValues = (readingRefs: Array<DisplayRef>): Array<EnhancedReading> => {
  // Create a derived atom that reads all the required reading/setting atoms
  // Use deep memo to prevent recreating the atom when readingRefs array reference changes
  const derivedAtom = useDeepMemo(() => {
    return atom((get) => {
      if (!readingRefs || readingRefs.length === 0) {
        return [];
      }

      return readingRefs
        .filter(
          (readingRef) =>
            readingRef.ref?.namespace !== Namespace.Setting && readingRef.ref !== undefined
        )
        .map((readingRef) => {
          const reading = get(readingsFamily({ pointId: readingRef.ref.id } as LatestValue));

          const rawValue = reading;
          const value = getLatestPrimitiveValue(reading);

          return {
            displayOptions: readingRef.displayOptions,
            displayType: readingRef.displayType,
            displayUnits: readingRef.displayUnits,
            key: readingRef.ref.id,
            label: readingRef.label,
            rawValue,
            ref: readingRef.ref,
            units: readingRef.ref.units,
            value,
            valueKind: reading?.kind,
          };
        });
    });
  }, [readingRefs]);

  return useAtomValue(derivedAtom);
};
